import { useState, useEffect } from 'react';
import { getUsers, getUserDetails, refreshUserRoles, updateAdminUser } from '../../services/adminService';
import UserDetailsModal from './users/UserDetailsModal';
import CreatePlaceholderUserModal from './users/CreatePlaceholderUserModal';
import { normalizeSearchText, matchesSearch } from '../../utils/searchHelpers';

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SOURCE_OPTIONS = ['other', 'socials', 'target', 'ads', 'friends', 'organic'];
const USERS_PER_PAGE = 100;

function formatRegularDays(regularDays) {
  if (!Array.isArray(regularDays) || regularDays.length === 0) return '';
  return [...regularDays].sort((a, b) => a - b).map((d) => DAY_SHORT[d]).filter(Boolean).join(', ');
}

function getDisplayName(user) {
  const first = user?.firstName || user?.given_name || user?.name || user?.nickname || '';
  const last = user?.lastName || user?.family_name || '';
  return `${first} ${last}`.trim();
}

function getCurrentOrRecentPassStatus(passes) {
  if (!passes?.length) return null;
  const statusOrder = { active: 0, exhausted: 1, expired: 2, cancelled: 3 };
  const sorted = [...passes].sort((a, b) => {
    const orderA = statusOrder[a.status] ?? 4;
    const orderB = statusOrder[b.status] ?? 4;
    if (orderA !== orderB) return orderA - orderB;
    return (new Date(b.expiresAt) || 0) - (new Date(a.expiresAt) || 0);
  });
  return sorted[0]?.status ?? null;
}

function PassStatusBadge({ status }) {
  if (!status) return <span className="text-gray-400">—</span>;
  const styles = {
    active: 'bg-green-100 text-green-700',
    exhausted: 'bg-amber-100 text-amber-700',
    expired: 'bg-gray-100 text-gray-600',
    cancelled: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function SourceBadge({ source }) {
  if (!source) return null;
  const styles = {
    other: 'bg-slate-100 text-slate-700',
    socials: 'bg-sky-100 text-sky-700',
    target: 'bg-purple-100 text-purple-700',
    ads: 'bg-blue-100 text-blue-700',
    friends: 'bg-orange-100 text-orange-700',
    organic: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold leading-none ${styles[source] || 'bg-gray-100 text-gray-600'}`}>
      {source}
    </span>
  );
}

export default function AdminUsers({ token }) {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [refreshingRoles, setRefreshingRoles] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState(null);
  const [openSourceForUser, setOpenSourceForUser] = useState(null);
  const [savingSourceUserId, setSavingSourceUserId] = useState(null);
  const [editingNotesForUser, setEditingNotesForUser] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNotesUserId, setSavingNotesUserId] = useState(null);
  const [showCreatePlaceholder, setShowCreatePlaceholder] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [token]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (!openSourceForUser) return undefined;
    const handleOutsideClick = (event) => {
      if (event.target.closest('[data-source-chooser="true"]')) return;
      setOpenSourceForUser(null);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [openSourceForUser]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Load all users once, then search/filter in frontend.
      const collectedUsers = [];
      let nextPage = 1;
      let totalPages = 1;
      do {
        const data = await getUsers(token, nextPage, '', USERS_PER_PAGE);
        collectedUsers.push(...(data.users || []));
        totalPages = data?.pagination?.pages || 1;
        nextPage += 1;
      } while (nextPage <= totalPages);
      setUsers(collectedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (userId) => {
    setSelectedUserId(userId);
    setDetailsLoading(true);
    try {
      const data = await getUserDetails(token, userId);
      setUserDetails(data);
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setSelectedUserId(null);
    setUserDetails(null);
  };

  const handleRefreshRoles = async () => {
    setRefreshingRoles(true);
    setRefreshMessage(null);
    try {
      const data = await refreshUserRoles(token);
      await fetchUsers();
      const parts = [];
      if (data.passesExpired > 0) parts.push(`${data.passesExpired} pass(es) marked expired`);
      if (data.roleChanged > 0) parts.push(`${data.roleChanged} role(s) updated`);
      if (parts.length === 0) parts.push(`Checked ${data.total} user(s). No changes needed.`);
      setRefreshMessage(parts.join('. '));
      setTimeout(() => setRefreshMessage(null), 5000);
    } catch (error) {
      console.error('Error refreshing roles:', error);
      setRefreshMessage('Failed to refresh roles.');
    } finally {
      setRefreshingRoles(false);
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    const aTime = new Date(a.created_at || a.createdAt || 0).getTime();
    const bTime = new Date(b.created_at || b.createdAt || 0).getTime();
    return bTime - aTime; // newest first
  });

  const normalizedSearch = normalizeSearchText(search);
  const filteredUsers = normalizedSearch
    ? sortedUsers.filter((user) => {
        const displayName = getDisplayName(user);
        return matchesSearch(search, [displayName]);
      })
    : sortedUsers;
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pagination = {
    page: safePage,
    pages: totalPages,
  };
  const paginatedUsers = filteredUsers.slice(
    (safePage - 1) * USERS_PER_PAGE,
    safePage * USERS_PER_PAGE
  );

  const handleChangeSource = async (user, nextSource) => {
    if (!user?.user_id || user.userSource === nextSource) {
      setOpenSourceForUser(null);
      return;
    }
    setSavingSourceUserId(user.user_id);
    try {
      await updateAdminUser(token, user.user_id, { userSource: nextSource });
      setUsers((prev) =>
        prev.map((u) => (u.user_id === user.user_id ? { ...u, userSource: nextSource } : u))
      );
      if (selectedUserId === user.user_id) {
        handleViewDetails(user.user_id);
      }
    } catch (error) {
      console.error('Error updating source:', error);
    } finally {
      setSavingSourceUserId(null);
      setOpenSourceForUser(null);
    }
  };

  const startEditingNotes = (user) => {
    setEditingNotesForUser(user.user_id);
    setNoteDraft(user.notes || '');
  };

  const cancelEditingNotes = () => {
    setEditingNotesForUser(null);
    setNoteDraft('');
  };

  const saveNotes = async (user) => {
    if (!user?.user_id) return;
    setSavingNotesUserId(user.user_id);
    try {
      await updateAdminUser(token, user.user_id, { notes: noteDraft });
      setUsers((prev) =>
        prev.map((u) => (u.user_id === user.user_id ? { ...u, notes: noteDraft } : u))
      );
      if (selectedUserId === user.user_id) {
        handleViewDetails(user.user_id);
      }
      setEditingNotesForUser(null);
      setNoteDraft('');
    } catch (error) {
      console.error('Error updating notes:', error);
    } finally {
      setSavingNotesUserId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="mb-4 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg w-full sm:flex-1 sm:min-w-[200px]"
        />
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => setShowCreatePlaceholder(true)}
            className="px-4 py-2 border border-brown text-brown rounded-lg text-sm font-semibold hover:bg-amber-50 w-full sm:w-auto"
          >
            Add placeholder user
          </button>
          <button
            type="button"
            onClick={handleRefreshRoles}
            disabled={refreshingRoles}
            className="px-4 py-2 bg-brown text-white rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50 w-full sm:w-auto"
          >
            {refreshingRoles ? 'Refreshing…' : 'Refresh roles'}
          </button>
          {refreshMessage && (
            <span className="text-sm text-gray-600 w-full sm:w-auto">{refreshMessage}</span>
          )}
        </div>
      </div>

      {/* Desktop: Table */}
      <div className="hidden md:block overflow-x-auto -mx-2 sm:mx-0">
        <table className="w-full text-left min-w-[720px]">
          <thead className="border-b border-gray-200">
            <tr>
              <th className="py-3 px-2 font-semibold">Name</th>
              <th className="py-3 px-2 font-semibold">Email</th>
              <th className="py-3 px-2 font-semibold">Role</th>
              <th className="py-3 px-2 font-semibold">Source</th>
              <th className="py-3 px-2 font-semibold">Regular days</th>
              <th className="py-3 px-2 font-semibold">Pass status</th>
              <th className="py-3 px-2 font-semibold">Passes</th>
              <th className="py-3 px-2 font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((user) => (
              <tr
                key={user._id}
                role="button"
                tabIndex={0}
                onClick={() => handleViewDetails(user.user_id)}
                onKeyDown={(e) => e.key === 'Enter' && handleViewDetails(user.user_id)}
                className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
              >
                <td className="py-3 px-2 text-sm">
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span>{getDisplayName(user)}</span>
                    {user.user_id?.startsWith('local|') ? (
                      <span className="shrink-0 text-[10px] uppercase tracking-wide text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded font-semibold">
                        Placeholder
                      </span>
                    ) : null}
                  </span>
                </td>
                <td className="py-3 px-2 text-sm">{user.email}</td>
                <td className="py-3 px-2 text-sm">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                    {user.role}
                  </span>
                </td>
                <td className="py-3 px-2 text-sm relative" data-source-chooser="true">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenSourceForUser((prev) => (prev === user.user_id ? null : user.user_id));
                    }}
                    className="cursor-pointer inline-flex items-center min-h-[24px] min-w-[72px]"
                  >
                    {user.userSource ? (
                      <SourceBadge source={user.userSource} />
                    ) : (
                      <span className="inline-block h-6 w-[72px] rounded-full border border-dashed border-gray-200 bg-gray-50/70" />
                    )}
                  </button>
                  {openSourceForUser === user.user_id && (
                    <div
                      className="absolute z-20 mt-2 p-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[120px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {SOURCE_OPTIONS.filter((option) => option !== user.userSource).map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => handleChangeSource(user, option)}
                          disabled={savingSourceUserId === user.user_id}
                          className="w-full text-left px-2 py-1 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                          <SourceBadge source={option} />
                        </button>
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-3 px-2 text-sm text-gray-700">{formatRegularDays(user.regularDays)}</td>
                <td className="py-3 px-2 text-sm">
                  <PassStatusBadge status={getCurrentOrRecentPassStatus(user.passes)} />
                </td>
                <td className="py-3 px-2 text-sm">{user.passes?.length || 0}</td>
                <td className="py-3 px-2 text-sm text-gray-600 max-w-[220px]">
                  {editingNotesForUser === user.user_id ? (
                    <div
                      className="space-y-1"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs min-h-[64px]"
                        placeholder="Add notes"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => saveNotes(user)}
                          disabled={savingNotesUserId === user.user_id}
                          className="text-xs px-2 py-1 rounded bg-brown text-white disabled:opacity-50"
                        >
                          {savingNotesUserId === user.user_id ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditingNotes}
                          disabled={savingNotesUserId === user.user_id}
                          className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditingNotes(user);
                      }}
                      className="text-left w-full"
                      title={user.notes || ''}
                    >
                      {user.notes ? (
                        <span className="line-clamp-2 break-words">{user.notes}</span>
                      ) : (
                        <span className="inline-block min-h-[18px] min-w-[72px]" />
                      )}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: Cards */}
      <div className="md:hidden space-y-3">
        {paginatedUsers.map((user) => (
          <div
            key={user._id}
            role="button"
            tabIndex={0}
            onClick={() => handleViewDetails(user.user_id)}
            onKeyDown={(e) => e.key === 'Enter' && handleViewDetails(user.user_id)}
            className="border border-gray-200 rounded-lg p-4 bg-white cursor-pointer hover:bg-gray-50 active:bg-gray-100"
          >
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate flex flex-wrap items-center gap-1.5">
                  <span className="truncate">{getDisplayName(user)}</span>
                  {user.user_id?.startsWith('local|') ? (
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded font-semibold">
                      Placeholder
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                {user.role}
              </span>
              <span className="text-xs text-gray-600">
                {user.passes?.length || 0} pass{user.passes?.length !== 1 ? 'es' : ''}
              </span>
              <PassStatusBadge status={getCurrentOrRecentPassStatus(user.passes)} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600" data-source-chooser="true">
              <span className="inline-flex items-center gap-1"><strong className="text-gray-500">Source:</strong></span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSourceForUser((prev) => (prev === user.user_id ? null : user.user_id));
                }}
                className="inline-flex items-center"
              >
                {user.userSource ? (
                  <SourceBadge source={user.userSource} />
                ) : (
                  <span className="inline-block h-5 w-16 rounded-full border border-dashed border-gray-200 bg-gray-50/70" />
                )}
              </button>
              {formatRegularDays(user.regularDays) ? (
                <span><strong className="text-gray-500">Days:</strong> {formatRegularDays(user.regularDays)}</span>
              ) : null}
            </div>
            {openSourceForUser === user.user_id && (
              <div
                className="mt-2 p-2 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-wrap gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                {SOURCE_OPTIONS.filter((option) => option !== user.userSource).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleChangeSource(user, option)}
                    disabled={savingSourceUserId === user.user_id}
                    className="disabled:opacity-50"
                  >
                    <SourceBadge source={option} />
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              {editingNotesForUser === user.user_id ? (
                <div className="space-y-1">
                  <textarea
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-xs min-h-[72px]"
                    placeholder="Add notes"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => saveNotes(user)}
                      disabled={savingNotesUserId === user.user_id}
                      className="text-xs px-2 py-1 rounded bg-brown text-white disabled:opacity-50"
                    >
                      {savingNotesUserId === user.user_id ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditingNotes}
                      disabled={savingNotesUserId === user.user_id}
                      className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => startEditingNotes(user)}
                  className="text-left w-full"
                  title={user.notes || ''}
                >
                  {user.notes ? (
                    <p className="text-xs text-gray-600 line-clamp-2 break-words">
                      <strong className="text-gray-500">Notes:</strong> {user.notes}
                    </p>
                  ) : (
                    <span className="inline-block min-h-[18px] min-w-[72px]" />
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {loading && <p className="text-center py-4">Loading...</p>}

      {pagination && (
        <div className="flex flex-wrap gap-2 mt-4 justify-center items-center">
          <button
            type="button"
            onClick={() => safePage > 1 && setPage(safePage - 1)}
            disabled={safePage === 1}
            className="px-3 py-2 sm:px-4 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Previous
          </button>
          <span className="px-2 sm:px-4 py-2 text-sm">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            type="button"
            onClick={() => safePage < pagination.pages && setPage(safePage + 1)}
            disabled={safePage >= pagination.pages}
            className="px-3 py-2 sm:px-4 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Next
          </button>
        </div>
      )}

      {showCreatePlaceholder && (
        <CreatePlaceholderUserModal
          token={token}
          onClose={() => setShowCreatePlaceholder(false)}
          onCreated={async (user) => {
            await fetchUsers();
            if (user?.user_id) handleViewDetails(user.user_id);
          }}
        />
      )}

      {/* User Details Modal */}
      {selectedUserId && (
        <UserDetailsModal
          userDetails={userDetails}
          detailsLoading={detailsLoading}
          onClose={closeDetailsModal}
          onRefresh={() => handleViewDetails(selectedUserId)}
          onUserUpdated={fetchUsers}
          onMergeComplete={async (keptUserId) => {
            await fetchUsers();
            if (keptUserId) handleViewDetails(keptUserId);
            else closeDetailsModal();
          }}
        />
      )}
    </div>
  );
}